/**
 * Metric-store row-key contract (tech-arch §5.1):
 *
 *   {tenantId}#{resourceId}#{datapointPath}#{reversedTimestamp}
 *
 * Tenant-isolated, time-descending scans. The BigQuery MVP implementation
 * and the P1 Bigtable promotion share this contract so promoting the hot
 * store changes no callers (§11.3).
 */
const SEPARATOR = '#';
/**
 * Charset contracts mirrored from the shared Zod schemas (kept literal
 * here so utils stay dependency-free; source of truth documented):
 *  - ids / tenant ids: common.schema.ts idSchema/tenantIdSchema
 *  - sensor keys: sensor.schema.ts sensorKeySchema
 *  - datapoints: sensor.schema.ts datapointSchema.name
 */
const ID_PATTERN = /^[A-Za-z0-9_-]{1,256}$/;
const TENANT_ID_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;
const SENSOR_KEY_PATTERN = /^[a-z][a-z0-9_.-]{0,127}$/;
const DATAPOINT_PATTERN = /^[A-Za-z][A-Za-z0-9_.]{0,127}$/;
function assertIdComponent(name, value) {
    const pattern = name === 'tenantId' ? TENANT_ID_PATTERN : ID_PATTERN;
    if (!pattern.test(value)) {
        throw new Error(`row-key component "${name}" must match ${pattern} (got: ${value})`);
    }
}
/** 9999-12-31T23:59:59.999Z in epoch ms — upper bound for reversal.
 *  Exported so wire schemas can enforce the same ceiling at ingest time. */
export const MAX_TIMESTAMP_MS = 253402300799999;
/** Fixed width of the reversed-timestamp component. */
const TS_WIDTH = String(MAX_TIMESTAMP_MS).length;
function assertKeyComponent(name, value) {
    if (value.length === 0) {
        throw new Error(`row-key component "${name}" must not be empty`);
    }
    if (value.includes(SEPARATOR)) {
        throw new Error(`row-key component "${name}" must not contain "${SEPARATOR}" (got: ${value})`);
    }
}
/**
 * Datapoint path within a resource: `{sensorKey}/{instanceId|-}/{datapoint}`.
 * Uses `/` internally so the whole path is one `#`-delimited component.
 *
 * `"-"` is RESERVED as the no-instance sentinel: a real instanceId equal to
 * `"-"` is rejected so "no instance" and an instance named "-" can never
 * alias each other in the store.
 */
export function buildDatapointPath(sensorKey, instanceId, datapoint) {
    if (!SENSOR_KEY_PATTERN.test(sensorKey)) {
        throw new Error(`sensorKey must match ${SENSOR_KEY_PATTERN} (got: ${sensorKey})`);
    }
    if (!DATAPOINT_PATTERN.test(datapoint)) {
        throw new Error(`datapoint must match ${DATAPOINT_PATTERN} (got: ${datapoint})`);
    }
    if (instanceId === '-') {
        throw new Error('instanceId "-" is reserved as the no-instance sentinel; pass null');
    }
    if (instanceId !== null) {
        assertIdComponent('instanceId', instanceId);
    }
    return `${sensorKey}/${instanceId ?? '-'}/${datapoint}`;
}
/** Zero-padded reversed timestamp so lexicographic order = time-descending. */
export function reverseTimestamp(timestampMs) {
    if (!Number.isInteger(timestampMs) || timestampMs < 0) {
        throw new Error(`timestampMs must be a non-negative integer (got: ${timestampMs})`);
    }
    if (timestampMs > MAX_TIMESTAMP_MS) {
        throw new Error(`timestampMs exceeds maximum supported value (got: ${timestampMs})`);
    }
    return String(MAX_TIMESTAMP_MS - timestampMs).padStart(TS_WIDTH, '0');
}
function assertDatapointPath(datapointPath) {
    const segments = datapointPath.split('/');
    if (segments.length !== 3 || segments.some((s) => s.length === 0)) {
        throw new Error(`datapointPath must be {sensorKey}/{instanceId|-}/{datapoint} (got: ${datapointPath})`);
    }
    // Segment-level charset contracts — build and parse share this, so
    // every key the builder accepts is guaranteed to round-trip.
    const [sensorKey, instanceSegment, datapoint] = segments;
    if (!SENSOR_KEY_PATTERN.test(sensorKey)) {
        throw new Error(`invalid sensorKey segment: ${sensorKey}`);
    }
    if (instanceSegment !== '-' && !ID_PATTERN.test(instanceSegment)) {
        throw new Error(`invalid instanceId segment: ${instanceSegment}`);
    }
    if (!DATAPOINT_PATTERN.test(datapoint)) {
        throw new Error(`invalid datapoint segment: ${datapoint}`);
    }
}
export function buildMetricRowKey(tenantId, resourceId, datapointPath, timestampMs) {
    assertIdComponent('tenantId', tenantId);
    assertIdComponent('resourceId', resourceId);
    assertKeyComponent('datapointPath', datapointPath);
    // Build enforces the same canonical path shape parse enforces, so every
    // built key is guaranteed to round-trip.
    assertDatapointPath(datapointPath);
    return [
        tenantId,
        resourceId,
        datapointPath,
        reverseTimestamp(timestampMs),
    ].join(SEPARATOR);
}
export function parseMetricRowKey(rowKey) {
    const parts = rowKey.split(SEPARATOR);
    if (parts.length !== 4) {
        throw new Error(`invalid metric row key (expected 4 components): ${rowKey}`);
    }
    const [tenantId, resourceId, datapointPath, reversed] = parts;
    // Fail-closed: components must satisfy the same charset contracts the
    // schemas enforce — a malformed (potentially cross-tenant) key must
    // never parse into usable parts.
    assertIdComponent('tenantId', tenantId);
    assertIdComponent('resourceId', resourceId);
    assertKeyComponent('datapointPath', datapointPath);
    // The datapoint path must satisfy the {sensorKey}/{instanceId|-}/{datapoint}
    // contract with schema-conformant segments (shared with the builder).
    assertDatapointPath(datapointPath);
    if (!/^\d+$/.test(reversed) || reversed.length !== TS_WIDTH) {
        throw new Error(`invalid reversed-timestamp component: ${reversed}`);
    }
    const timestampMs = MAX_TIMESTAMP_MS - Number(reversed);
    if (timestampMs < 0) {
        throw new Error(`reversed timestamp out of range: ${reversed}`);
    }
    return { tenantId, resourceId, datapointPath, timestampMs };
}
//# sourceMappingURL=row-key.js.map